import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PWAInstallProvider } from "@/contexts/PWAInstallContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import UploadPage from "@/pages/Upload";
import UsersPage from "@/pages/admin/Users";
import DepartmentsPage from "@/pages/admin/Departments";
import UploadsHistory from "@/pages/admin/Uploads";
import Dashboard from "@/pages/Dashboard";
import Ganador from "@/pages/reports/Ganador";
import Ranking from "@/pages/reports/Ranking";
import Comparativo from "@/pages/reports/Comparativo";
import Tendencia from "@/pages/reports/Tendencia";
import Distribucion from "@/pages/reports/Distribucion";
import Heatmap from "@/pages/reports/Heatmap";
import Tabla from "@/pages/reports/Tabla";
import Efficiency from "@/pages/reports/Efficiency";
import ReportsHub from "@/pages/reports/ReportsHub";
import TypeComparison from "@/pages/reports/TypeComparison";
import ComparativesList from "@/pages/comparatives/ComparativesList";
import EmployeesCanonical from "@/pages/admin/EmployeesCanonical";
import ShareLinks from "@/pages/admin/Share";
import AdminDistributionScope from "@/pages/admin/DistributionScope";
import AdminLayout from "@/pages/admin/AdminLayout";
import Schedule from "@/pages/Schedule";
import ScheduleSections from "@/pages/admin/ScheduleSections";
import Catalog from "@/pages/Catalog";
import CatalogDetail from "@/pages/CatalogDetail";
import Inventory from "@/pages/Inventory";
import InventoryReview from "@/pages/InventoryReview";
import Help from "@/pages/Help";
import EmployeeDetail from "@/pages/EmployeeDetail";
import { DrillDownProvider } from "@/components/drilldown/DrillDownContext";
import ProductDetailModal from "@/components/drilldown/ProductDetailModal";
import OrderDetailModal from "@/components/drilldown/OrderDetailModal";
import OnboardingTour from "@/components/OnboardingTour";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import UpdateBanner from "@/components/UpdateBanner";
// Public share viewer is loaded lazily because it pulls Recharts/heavy deps
// and is meant for anonymous viewers (no AuthProvider context required).
const PublicShare = lazy(() => import("@/pages/share/PublicShare"));
const PublicComparative = lazy(() => import("@/pages/comparatives/PublicComparative"));
const PublicInventory = lazy(() => import("@/pages/share/PublicInventoryPage"));
// Plotly is heavy (~3MB) — load only when /reports/3d is visited.
const Vista3D = lazy(() => import("@/pages/reports/Vista3D"));
import "@/i18n";

function Vista3DLazy() {
  return (
    <Suspense
      fallback={
        <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
          Loading 3D engine…
        </div>
      }
    >
      <Vista3D />
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <PWAInstallProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            {/* Anonymous public viewer — no auth, no layout/sidebar. */}
            <Route
              path="/share/:token"
              element={
                <Suspense
                  fallback={
                    <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                      Loading…
                    </div>
                  }
                >
                  <PublicShare />
                </Suspense>
              }
            />
            {/* Phase 5.6 — Public comparative share, no auth */}
            <Route
              path="/public/comparatives/:token"
              element={
                <Suspense
                  fallback={
                    <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                      Cargando…
                    </div>
                  }
                >
                  <PublicComparative />
                </Suspense>
              }
            />
            {/* Phase 7.1 — Public inventory dashboard, no auth */}
            <Route
              path="/public/inventory/:token"
              element={
                <Suspense
                  fallback={
                    <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                      Cargando…
                    </div>
                  }
                >
                  <PublicInventory />
                </Suspense>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <ProtectedRoute>
                  <DrillDownProvider>
                    <OnboardingTour />
                    <Layout />
                    {/* Phase 9.1 — Global drill-down modals, rendered once */}
                    <ProductDetailModal />
                    <OrderDetailModal />
                  </DrillDownProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="/upload"
                element={
                  <ProtectedRoute requirePermission="upload">
                    <UploadPage />
                  </ProtectedRoute>
                }
              />
              {/* Reports */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requirePermission="report:kpis">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              {/* Phase 9.1 — Employee drill-down page */}
              <Route
                path="/employees/:canonical_id"
                element={
                  <ProtectedRoute requirePermission="report:kpis">
                    <EmployeeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/ganador"
                element={
                  <ProtectedRoute requirePermission="report:ganador">
                    <Ganador />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schedule"
                element={
                  <ProtectedRoute requirePermission="schedule:view">
                    <Schedule />
                  </ProtectedRoute>
                }
              />

              {/* Consolidated reports hub — the 7 sub-views share a sticky
                  tab bar. Each child <Route> stays at its original URL so
                  bookmarks and share links keep working. */}
              <Route path="/reports" element={<ReportsHub />}>
                <Route
                  path="ranking"
                  element={
                    <ProtectedRoute requirePermission="report:ranking">
                      <Ranking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="comparativo"
                  element={
                    <ProtectedRoute requirePermission="report:comparativo">
                      <Comparativo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tendencia"
                  element={
                    <ProtectedRoute requirePermission="report:tendencia">
                      <Tendencia />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="distribucion"
                  element={
                    <ProtectedRoute requirePermission="report:distribucion">
                      <Distribucion />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="type-comparison"
                  element={
                    <ProtectedRoute requirePermission="report:comparativo">
                      <TypeComparison />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="heatmap"
                  element={
                    <ProtectedRoute requirePermission="report:heatmap">
                      <Heatmap />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="tabla"
                  element={
                    <ProtectedRoute requirePermission="report:tabla">
                      <Tabla />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="3d"
                  element={
                    <ProtectedRoute requirePermission="report:3d">
                      <Vista3DLazy />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="/reports/efficiency"
                element={
                  <ProtectedRoute requirePermission="report:efficiency">
                    <Efficiency />
                  </ProtectedRoute>
                }
              />
              {/* Phase 5.9 — Consolidated Admin layout with horizontal tabs. */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/usuarios" replace />} />
                <Route
                  path="usuarios"
                  element={
                    <ProtectedRoute requirePermission="users:manage">
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="departamentos"
                  element={
                    <ProtectedRoute requirePermission="dept:manage">
                      <DepartmentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="empleados"
                  element={
                    <ProtectedRoute requirePermission="employees:manage">
                      <EmployeesCanonical />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="scope"
                  element={
                    <ProtectedRoute requirePermission="employees:manage">
                      <AdminDistributionScope />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="secciones-horario"
                  element={
                    <ProtectedRoute requirePermission="schedule:manage">
                      <ScheduleSections />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="links-publicos"
                  element={
                    <ProtectedRoute requirePermission="share:manage">
                      <ShareLinks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="historial-cargas"
                  element={
                    <ProtectedRoute requirePermission="users:manage">
                      <UploadsHistory />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Phase 5.9 — Backward-compat redirects for the legacy admin URLs. */}
              <Route path="/admin/users" element={<Navigate to="/admin/usuarios" replace />} />
              <Route path="/admin/departments" element={<Navigate to="/admin/departamentos" replace />} />
              <Route path="/admin/employees" element={<Navigate to="/admin/empleados" replace />} />
              <Route path="/admin/distribution-scope" element={<Navigate to="/admin/scope" replace />} />
              <Route path="/admin/schedule-sections" element={<Navigate to="/admin/secciones-horario" replace />} />
              <Route path="/admin/share" element={<Navigate to="/admin/links-publicos" replace />} />
              <Route path="/admin/public-links" element={<Navigate to="/admin/links-publicos" replace />} />
              <Route path="/admin/uploads" element={<Navigate to="/admin/historial-cargas" replace />} />
              <Route path="/admin/upload-history" element={<Navigate to="/admin/historial-cargas" replace />} />
              {/* Phase 5 — Catalog */}
              <Route
                path="/catalog"
                element={
                  <ProtectedRoute requirePermission="catalog:view">
                    <Catalog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalog/:sku"
                element={
                  <ProtectedRoute requirePermission="catalog:view">
                    <CatalogDetail />
                  </ProtectedRoute>
                }
              />
              {/* Phase 6.1 — Inventory dashboard */}
              <Route
                path="/inventory"
                element={
                  <ProtectedRoute requirePermission="catalog:view">
                    <Inventory />
                  </ProtectedRoute>
                }
              />
              {/* Phase 7.2 — Admin review of needs_user_review movements */}
              <Route
                path="/inventory/review"
                element={
                  <ProtectedRoute requirePermission="catalog:view">
                    <InventoryReview />
                  </ProtectedRoute>
                }
              />
              {/* Phase 5.4 — Comparatives standalone module */}
              <Route
                path="/comparatives"
                element={
                  <ProtectedRoute requirePermission="report:comparativo">
                    <ComparativesList />
                  </ProtectedRoute>
                }
              />
              {/* Phase 4 — Help center (any authenticated user) */}
              <Route path="/help" element={<Help />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PWAInstallBanner />
          <UpdateBanner />
        </BrowserRouter>
        </AuthProvider>
      </PWAInstallProvider>
    </ThemeProvider>
  );
}

export default App;
